import styled from 'styled-components'

import { COLORS, SIZES, FONT_WEIGHTS, COLORS2 } from '~/common/globalStyles'
import { Theme } from '@mui/material'

export interface TextProps {
  uppercase?: boolean
  color?: string
  size?: keyof typeof SIZES
  lineHeight?: string
  weight?: keyof typeof FONT_WEIGHTS
  fontStyle?: string
  font?: string
  strikethrough?: boolean
  underline?: boolean
  letterSpacing?: string
}

const calculateDirectColor = (color?: string) => {
  return color && COLORS[color as keyof typeof COLORS]
    ? COLORS[color as keyof typeof COLORS]
    : color
      ? color
      : 'currentColor'
}

const calculateTextColor = (theme: Theme, color?: string) => {
  const themeName = theme?.name || 'default'
  if (themeName !== 'lightV2') {
    return calculateDirectColor(color)
  }
  const { palette } = theme
  switch (color) {
    case 'lighterGray':
      return palette.grey[100]

    case 'lightGray':
    case 'gray350':
      return palette.grey[300]

    case 'lighterMediumGray':
      return palette.grey[400]

    case 'gray450':
    case 'mediumGray':
    case 'gray6':
    case COLORS.gray6:
      return palette.grey[500]

    case COLORS.gray5:
    case 'gray5':
      return palette.grey[700]

    case 'darkGray':
      return palette.grey[800]

    case COLORS.lightRed:
    case 'lightRed':
      return palette.red[300]

    case 'red':
      return palette.red[500]

    case COLORS2.orange[400]:
      return palette.orange[400]

    case 'mediumGreen':
    case 'green':
      return palette.green[500]

    case 'blue':
    case COLORS2.blue[500]:
      // blue was primary color of old theme, we will return as blue is not used in 2022 theme
      return palette.primary.main

    case 'yellow2':
    case 'yellowWarning':
      return palette.warning.main

    case 'black':
      return palette.common.black

    case 'white':
      return palette.common.white

    default:
      return calculateDirectColor(color)
  }
}

const Text = styled.span<TextProps>(
  ({
    theme,
    color,
    size = theme?.name !== 'lightV2' ? 'medium' : 'fourteen',
    lineHeight = 1,
    weight,
    font,
    uppercase,
    fontStyle,
    strikethrough,
    underline,
    letterSpacing,
  }) => `
  color: ${calculateTextColor(theme, color)};
  font-size: ${size !== 'medium' ? SIZES[size] + '!important' : SIZES.medium};
  line-height: ${lineHeight};
  font-weight: ${FONT_WEIGHTS[weight || 'normal']};
  font-family: ${font || 'inherit'};
  text-transform: ${uppercase ? 'uppercase' : 'none'};
  font-style: ${fontStyle};
  ${
    strikethrough
      ? `
      text-decoration: line-through;
      color: ${theme.palette.action.disabled};
    `
      : ''
  };
  ${letterSpacing ? `letter-spacing: ${letterSpacing};` : ''}
  ${underline ? `text-decoration: underline;` : ''}
`,
)

Text.defaultProps = {
  size: undefined,
  color: undefined,
  uppercase: false,
  weight: 'normal',
  lineHeight: '1',
  fontStyle: 'normal',
  strikethrough: false,
}

/** @component */
export default Text
